/**
 * BrandHero — Header compacto del Manual de tu IA.
 *
 * Versión simplificada del hero anterior:
 *  - 1 sola fila horizontal (no se apila en lg)
 *  - Logo + nombre + chip vertical + chips inline de resumen
 *  - Saca el CTA "Probar cómo suena" (ahora el simulador tiene su tab propia)
 *  - Saca chip de presupuesto (eliminado del modelo)
 *  - Más compacto: ~80px de alto en vez de ~140px
 */

import { Sparkles, Globe, Store, Clock, Mic, Scale } from "lucide-react";
import type { DeenexMultiMarca } from "../../../hooks/useIntegracion";

interface BrandHeroProps {
    marca: DeenexMultiMarca | null;
    tone: number;
    aggressiveness: number;
    sendWindow: { start: string; end: string };
}

function toneLabel(tone: number): string {
    if (tone < 30) return "Formal";
    if (tone > 70) return "Amigable";
    return "Neutral";
}

function aggressivenessLabel(a: number): string {
    if (a < 40) return "Conservadora";
    if (a > 70) return "Agresiva";
    return "Balanceada";
}

export default function BrandHero({
    marca,
    tone,
    aggressiveness,
    sendWindow,
}: BrandHeroProps) {
    const name = marca?.appName || "Tu marca";
    const initials = name
        .split(" ")
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
    const vertical = (marca as any)?.vertical;
    const localesCount = (marca as any)?.localesCount;

    return (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 relative overflow-hidden">
            {/* Decorative gradient blob */}
            <div className="absolute -top-16 -right-16 w-72 h-72 bg-gradient-to-br from-violet-200/30 to-fuchsia-200/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative flex items-center gap-4 flex-wrap">
                {/* Logo */}
                {marca?.appLogo || marca?.images?.logo ? (
                    <img
                        src={marca.appLogo || marca.images?.logo}
                        alt={name}
                        className="w-14 h-14 rounded-2xl object-cover bg-white shadow-md ring-2 ring-white flex-shrink-0"
                    />
                ) : (
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-md ring-2 ring-white bg-gradient-to-br from-violet-500 to-fuchsia-500 flex-shrink-0">
                        {initials}
                    </div>
                )}

                {/* Identidad */}
                <div className="min-w-0 flex-shrink">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100 uppercase tracking-wider flex items-center gap-1">
                            <Sparkles size={9} />
                            Manual de tu IA
                        </span>
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 tracking-tight truncate">
                        {name}
                    </h1>
                    <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-0.5 flex-wrap">
                        {vertical && (
                            <span className="text-slate-600">{vertical}</span>
                        )}
                        {marca?.domain && (
                            <>
                                <span className="text-slate-300">·</span>
                                <span className="flex items-center gap-1">
                                    <Globe size={10} />
                                    {marca.domain}
                                </span>
                            </>
                        )}
                        {localesCount > 0 && (
                            <>
                                <span className="text-slate-300">·</span>
                                <span className="flex items-center gap-1">
                                    <Store size={10} />
                                    {localesCount} sucursales
                                </span>
                            </>
                        )}
                    </div>
                </div>

                {/* Divider vertical */}
                <div className="hidden lg:block h-12 w-px bg-slate-200 mx-2" />

                {/* Resumen vivo de reglas */}
                <div className="flex items-center gap-2 flex-wrap ml-auto">
                    <SummaryChip
                        icon={<Mic size={10} />}
                        label="Tono"
                        value={toneLabel(tone)}
                    />
                    <SummaryChip
                        icon={<Scale size={10} />}
                        label="Estrategia"
                        value={aggressivenessLabel(aggressiveness)}
                    />
                    <SummaryChip
                        icon={<Clock size={10} />}
                        label="Horario"
                        value={`${sendWindow.start}–${sendWindow.end}`}
                    />
                </div>
            </div>
        </div>
    );
}

function SummaryChip({
    icon,
    label,
    value,
}: {
    icon: React.ReactNode;
    label: string;
    value: string;
}) {
    return (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
            <span className="text-slate-400">{icon}</span>
            <div className="flex flex-col items-start">
                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 leading-none">
                    {label}
                </span>
                <span className="text-xs font-semibold text-slate-800 leading-tight">
                    {value}
                </span>
            </div>
        </div>
    );
}
